import { gsap, Back } from 'gsap';
import { CarFSBox } from './CarFSBox';
import { FsLeftBox } from './FsLeftBox';
import { ResizeHelper, IDimOrientation } from '../../dev/utils/ResizeHelper';
import { StageLayer } from "@nolimitcity/slot-launcher/bin/StageLayer";
import { Helper } from '../../dev/utils/Helper';
import { WaysCounter } from '../independent/countup/WaysCounter';
import { CarTypes } from '../server/data/GameDataModels';
import { MathHelper } from '../../dev/utils/MathHelper';
import { RoadRage } from '../RoadRage';
import { SoundManager } from '../config/sound/SoundManager';
import { SoundConfig } from '../config/sound/SoundConfig';

export class BackgroundOverlay extends PIXI.Container implements StageLayer {

    private bg_WIDTH: number = 1680;
    private bg_HEIGHT: number = 1680;

    protected readonly positionDim:IDimOrientation = {
        landscape :{
            scale:      new PIXI.Point(1,1),
            position:   new PIXI.Point(0,0),
        },
        portrait :{
            scale:      new PIXI.Point(1,1),
            position:   new PIXI.Point(0,0)
        }
    };

    private carBoxes:CarFSBox[] = [];

    private freeSpinsCollector:FsLeftBox;
    private numberOfBoxes:number = 3;
    protected _carBox: PIXI.Graphics;
    protected _timeline: gsap.core.Timeline;
    protected _wayCounter:WaysCounter;
    private spLogo: PIXI.Sprite;

    public get allBoxes():CarFSBox[]{
        return this.carBoxes;
    }

    public fetchBoxBytype(type:string):CarFSBox{
        if(type.length == 4) type = type.slice(1,4);
        
        switch(type){
            case CarTypes.RED:
                return this.redBox;
            case CarTypes.GREEN:
                return this.greenBox;
            default:
                return this.yellowBox;
        }
    }

    public get redBox():CarFSBox{
        return this.carBoxes[0];
    }

    public get yellowBox():CarFSBox{
        return this.carBoxes[1];
    }

    public get greenBox():CarFSBox{
        return this.carBoxes[2];
    }

   public get fsDummyBox():FsLeftBox{
        return this.freeSpinsCollector;
    }

    private get BoxStartPos():PIXI.Point{
        return ResizeHelper.isLandscape ? new PIXI.Point(770, 160 ) : new PIXI.Point(360 , 38)
    }

    constructor() {
        super();
        this.name = "BackgroundOverlay";
        this.init();
    }

    protected init(): void {
        for(let i = 0; i < this.numberOfBoxes;i++){
            this.carBoxes.push(this.addChild(new CarFSBox(["C","B","A"][i])));
        }

        this.spLogo = this.addChild(new PIXI.Sprite(RoadRage.assetLoader.getTextures("Game_Logo")[0]));
        this.spLogo.anchor.set(0.5, 0.5);

        this.carBoxes.forEach((c,i) => {
            const distance:number = i*145;
            c.onResize(MathHelper.pointAdd(this.BoxStartPos, ResizeHelper.isLandscape ? new PIXI.Point(0, distance ) : new PIXI.Point(distance , 0)));
        })

        this.freeSpinsCollector = this.addChild(new FsLeftBox());

        this._wayCounter = this.addChild(new WaysCounter());

        this.onResize();
    }

    private movingTL:gsap.core.Timeline;

    private _moveVal:number = 0;
    private set moveVal(val:number){ this._moveVal = val; } 

    public playIntroLogo(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = Helper.setTimeline();
        const origPosY = ResizeHelper.isLandscape ? 160 : -100;

        tl.add(() => { SoundManager.playSoundEffect(SoundConfig.GAME_INTRO_LOGO); })

        tl.fromTo(this.spLogo, { alpha: 0, y: "-=100" }, { y: origPosY, alpha: 1, ease: Back.easeOut });

        tl.eventCallback("onComplete", () => this.onResize());

        Helper.clicker(this, () => gsap.globalTimeline.timeScale(3))

        tl.add(() => {
            this.off("pointerdown");
            gsap.globalTimeline.timeScale(1);
            SoundManager.StartMainMusic(true);
        })

        return tl;
    }

    switchBgMovement(durr:number,fromLeft:boolean): void {
        const startX: number = 0;
        this.movingTL = Helper.setTimeline(this.movingTL, new gsap.core.Timeline({
            onUpdate:() =>{
                if(this.movingTL.progress() <= 0.5)
                    this.x = startX + Number(this._moveVal);
                else{
                    this.x = startX + (Number(this._moveVal) - Number(fromLeft ? -this.bg_WIDTH : this.bg_WIDTH));
                }
            }
        }));

        this.moveVal = startX;

        const startToX: number = 0 + (fromLeft ? -this.bg_WIDTH : this.bg_WIDTH);

        this.movingTL.to(this, { moveVal: startToX, duration: durr });
        
        this.movingTL.to(this, { alpha: 0, duration: durr/4},0)
        this.movingTL.to(this, { alpha: 1, duration: durr/2},durr/2)
    }

    public enableBox(type:string, val:boolean, reset?:boolean):gsap.core.Timeline{
        if(type.length == 4) type = type.slice(1,4);
        
        let enabledCarTl:gsap.core.Timeline;

        switch(type){
            case CarTypes.RED:
                enabledCarTl = this.redBox.enable(val,reset);
                break;
            case CarTypes.GREEN:
                enabledCarTl = this.greenBox.enable(val,reset);
                break;
            default:
                enabledCarTl = this.yellowBox.enable(val,reset);
        }

        let activeCount:number = 0;
        const durr:number= enabledCarTl.totalDuration();

        this.carBoxes.forEach((c,i) => {
            if(c.isEnabled){
                const distance:number = ResizeHelper.isLandscape ? activeCount*145 : 280 - activeCount*145;

                const newPos:PIXI.Point = MathHelper.pointAdd(this.BoxStartPos, ResizeHelper.isLandscape ? new PIXI.Point(0, distance ) : new PIXI.Point(distance , -10))

                if(!c.willBeEnabled){
                    c.position = newPos;
                }else
                    enabledCarTl.to(c.position,{y: newPos.y, x:newPos.x, duration:durr},0)

                activeCount++;
            }
        })

        return enabledCarTl;
    }

    onResize(): void {

        ResizeHelper.setDimForTarget(this, this.positionDim);

        let activeCount:number = 0;

        this.carBoxes.forEach((c,i) => {
            const distance:number = ResizeHelper.isLandscape ? activeCount*145 : 280 - activeCount*145;
            if(c.isEnabled) activeCount++;
            c.onResize(MathHelper.pointAdd(this.BoxStartPos, ResizeHelper.isLandscape ? new PIXI.Point(0, distance ) : new PIXI.Point(distance , -10)));
        })

        this._wayCounter.onResize();
        this.freeSpinsCollector.onResize();

        this.spLogo.visible = !ResizeHelper.isIPadd;

        if (ResizeHelper.isLandscape) {
            this.spLogo.scale.set(0.6);
            this.spLogo.position.set(-120, 160);
        } else {
            this.spLogo.scale.set(0.75);
            this.spLogo.position.set(360, -120);
        }
    }
}